import * as React from "react";
import { uniqueId } from "lodash-es";
import { Loader } from "@ic-anywhere/ic-components";

type PdfFetchInfo = {
  url: string;
  httpHeaders?: {
    authorization: string;
  };
  withCredentials?: boolean;
};

export interface PdfReaderProps {
  id?: string;
  pdfObject?: PdfFetchInfo;
  importPdfjsLib?: boolean;
  fixedCommands?: boolean;
  hideCommands?: boolean;
  hidePrintCommand?: boolean;
  hideFullScreenCommand?: boolean;
  hideRotationCommands?: boolean;
  hideZoomCommands?: boolean;
  canvasWidth?: number;
  canvasHeight?: number;
  canvasMaxHeight?: number;
  displayFullDocument?: boolean;
  i18n?: object;
  ref?: any;
  onPdfReaderReady?: (event: any) => void;
  onPdfReaderPdfLoaded?: (event: any) => void;
  onPdfReaderPageLoaded?: (event: any) => void;
  onPdfReaderPageRendered?: (event: any) => void;
}

export const PdfReader: React.FC<PdfReaderProps> = ({
  id,
  pdfObject,
  canvasHeight,
  canvasMaxHeight,
  canvasWidth,
  displayFullDocument,
  fixedCommands,
  hideCommands,
  hideFullScreenCommand,
  hidePrintCommand,
  hideRotationCommands,
  hideZoomCommands,
  i18n,
  importPdfjsLib,
  onPdfReaderReady,
  onPdfReaderPdfLoaded,
  onPdfReaderPageLoaded,
  onPdfReaderPageRendered,
}): React.ReactElement<any> => {
  const pdfReaderId = React.useRef(id || uniqueId("PdfReaderId"));
  const pdfReaderRef = React.useRef<any>(null);

  React.useEffect(() => {
    const node = document.getElementById(pdfReaderId.current);
    if (onPdfReaderReady) {
      node?.addEventListener("sgwt-pdf-reader--ready", onPdfReaderReady);
    }
    if (onPdfReaderPdfLoaded) {
      node?.addEventListener("sgwt-pdf-reader--pdf-loaded", onPdfReaderPdfLoaded);
    }
    if (onPdfReaderPageLoaded) {
      node?.addEventListener("sgwt-pdf-reader--page-loaded", onPdfReaderPageLoaded);
    }
    if (onPdfReaderPageRendered) {
      node?.addEventListener("sgwt-pdf-reader--page-rendered", onPdfReaderPageRendered);
    }
    return () => {
      if (onPdfReaderReady) {
        node?.removeEventListener("sgwt-pdf-reader--ready", onPdfReaderReady);
      }
      if (onPdfReaderPdfLoaded) {
        node?.removeEventListener("sgwt-pdf-reader--pdf-loaded", onPdfReaderPdfLoaded);
      }
      if (onPdfReaderPageLoaded) {
        node?.removeEventListener("sgwt-pdf-reader--page-loaded", onPdfReaderPageLoaded);
      }
      if (onPdfReaderPageRendered) {
        node?.removeEventListener("sgwt-pdf-reader--page-rendered", onPdfReaderPageRendered);
      }
    };
  }, [onPdfReaderReady, onPdfReaderPdfLoaded, onPdfReaderPageLoaded, onPdfReaderPageRendered]);

  React.useImperativeHandle(pdfReaderRef, () => ({
    firstPage: () => pdfReaderRef.current?.firstPage(),
    previousPage: () => pdfReaderRef.current?.previousPage(),
    nextPage: () => pdfReaderRef.current?.nextPage(),
    lastPage: () => pdfReaderRef.current?.lastPage(),
    zoomIn: () => pdfReaderRef.current?.zoomIn(),
    zoomOut: () => pdfReaderRef.current?.zoomOut(),
    zoomReset: () => pdfReaderRef.current?.zoomReset(),
    rotateLeft: () => pdfReaderRef.current?.rotateLeft(),
    rotateRight: () => pdfReaderRef.current?.rotateRight(),
    fullScreen: () => pdfReaderRef.current?.fullScreen(),
    print: () => pdfReaderRef.current?.print(),
  }));

  if (!pdfObject) {
    return <Loader isVisible={true} />;
  }

  return (
    <sgwt-pdf-reader
      id={pdfReaderId.current}
      ref={pdfReaderRef}
      pdf-url={JSON.stringify(pdfObject)}
      canvas-height={canvasHeight}
      canvas-max-height={canvasMaxHeight}
      canvas-width={canvasWidth}
      display-full-document={displayFullDocument}
      fixed-commands={fixedCommands}
      hide-commands={hideCommands}
      hide-full-screen-command={hideFullScreenCommand}
      hide-print-command={hidePrintCommand}
      hide-rotation-commands={hideRotationCommands}
      hide-zoom-commands={hideZoomCommands}
      {...(i18n ? { i18n: JSON.stringify(i18n) } : {})}
      import-pdfjs-lib={importPdfjsLib}
    />
  );
};
